import React, { useState } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    FormGroup,
    Label,
    Input,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SelectDispenseDateModal from '../../commonComponents/SelectDispenseDateModal.js';

function AddRxForm({
    data,
    dispensedt,
    setDispensedt,
    openDispenseDateModal,
    setOpenDispenseDateModal,
    onReturnAdded,
    onRxAdded,
    canCreate = true,
    manualMode = false,
    facId: manualFacId,
    isDestructionOnly = false,
}) {
    const row = data?.[0] && typeof data[0] === 'object' ? data[0] : {};
    const {
        patName,
        patID,
        drugLabelName,
        qtyDispensed,
        rxNo,
        dispenseDt,
        allowReturn,
        blockReturnReason,
        controlledDrug,
        facId: searchFacId,
    } = row;
    const searchRemainingQty =
        row.remainingQty ??
        row.RemainingQty ??
        row.remainingQuantity ??
        row.RemainingQuantity;
    const [error, setError] = useState(null);

    const resolveDispensedLimit = (source) => {
        const raw = manualMode ? source?.DispensedQuantity : qtyDispensed;
        if (raw === '' || raw === null || raw === undefined) return NaN;
        const n = Number(raw);
        return Number.isFinite(n) ? n : NaN;
    };
    const resolveRemainingLimit = (source) => {
        const raw = manualMode ? source?.RemainingQuantity : searchRemainingQty;
        if (raw === '' || raw === null || raw === undefined) return NaN;
        const n = Number(raw);
        return Number.isFinite(n) ? n : NaN;
    };

    const emptyToUndefined = (value, originalValue) =>
        typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : value;

    const OrderReturnValidationSchema = Yup.object({
        ...(manualMode
            ? {
                  ResidentName: Yup.string().required('Resident name is required'),
                  DrugName: Yup.string().required('Drug name is required'),
                  DispenseDate: Yup.string(),
                  DispensedQuantity: Yup.number()
                      .transform(emptyToUndefined)
                      .typeError('Dispensed quantity must be a number')
                      .positive('Dispensed quantity must be a positive number'),
                  RemainingQuantity: Yup.number()
                      .transform(emptyToUndefined)
                      .typeError('Remaining quantity must be a number')
                      .min(0, 'Remaining quantity cannot be negative'),
              }
            : {}),
        Amount: (manualMode
            ? Yup.number().transform(emptyToUndefined).required('Quantity is required')
            : Yup.number().required('Quantity is required')
        )
            .test(
                'is-less-than-dispensed',
                'Return quantity cannot be greater than dispensed quantity',
                function (value) {
                    const limit = resolveDispensedLimit(this.parent);
                    if (Number.isFinite(limit) && value > limit) {
                        return this.createError({
                            message: `Return quantity cannot be greater than dispensed quantity of ${limit}`,
                        });
                    }
                    return true;
                }
            )
            .test(
                'is-less-than-remaining',
                'Return quantity cannot be greater than remaining quantity',
                function (value) {
                    const limit = resolveRemainingLimit(this.parent);
                    if (Number.isFinite(limit) && limit === 0) {
                        return this.createError({ message: 'Remaining quantity is already zero, so you cannot add further quantity here' });
                    }
                    if (Number.isFinite(limit) && value > limit) {
                        return this.createError({
                            message: `Return quantity cannot be greater than remaining quantity of ${limit}`,
                        });
                    }
                    return true;
                }
            ),
    });

    const initialValues = {
        Amount: '',
        RxNumber: '',
        ResidentName: '',
        DrugName: '',
        DispenseDate: '',
        DispensedQuantity: '',
        RemainingQuantity: '',
    };

    const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm,
        setFieldValue,
    } = useFormik({
        initialValues,
        validationSchema: OrderReturnValidationSchema,
        onSubmit: (values) => {
            if (onReturnAdded) {
                const rowFacId = manualMode ? manualFacId : searchFacId;
                if (manualMode && !rowFacId) {
                    toast.error('You must select a facility from facility dropdown');
                    return;
                }
                onReturnAdded({
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    rxNumber: manualMode ? values.RxNumber : rxNo,
                    facId: rowFacId,
                    residentName: manualMode ? values.ResidentName : patName,
                    residentId: manualMode ? '' : patID,
                    drugName: manualMode ? values.DrugName : drugLabelName,
                    dispensedQuantity: manualMode
                        ? String(values.DispensedQuantity ?? '')
                        : String(qtyDispensed ?? ''),
                    remainingQuantity: manualMode
                        ? String(values.RemainingQuantity ?? '')
                        : String(searchRemainingQty ?? ''),
                    dispenseDate: manualMode ? values.DispenseDate : dispenseDt,
                    quantity: Number(values.Amount),
                    returnDate: new Date().toISOString(),
                    allowReturn: manualMode ? true : allowReturn,
                    blockReturnReason: manualMode ? '' : blockReturnReason,
                    controlledDrug: manualMode ? false : controlledDrug,
                    isManual: manualMode,
                    destructionOnly: manualMode ? true : isDestructionOnly,
                });
                toast.success('Item added successfully!');
            }
            resetForm();
            setError(null);
            if (onRxAdded) onRxAdded();
        },
    });

    const dispensedLimit = resolveDispensedLimit(values);
    const remainingLimit = resolveRemainingLimit(values);
    const isRemainingQtyZero = Number.isFinite(remainingLimit) && remainingLimit === 0;
    const remainingQtyErrorMessage = isRemainingQtyZero
        ? 'Remaining quantity is already zero, so you cannot add further quantity here'
        : `Return quantity cannot be greater than remaining quantity of ${remainingLimit}`;
    const isRemainingQtyError =
        Number.isFinite(remainingLimit) && values.Amount !== '' && Number(values.Amount) > remainingLimit;
    const isMissingManualFacility = manualMode && !manualFacId;
    const isManualEntryEmpty =
        manualMode && Object.values(values).every((value) => value === '' || value === null || value === undefined);
    const isManualRequiredFieldsMissing =
        manualMode &&
        (!values.ResidentName?.trim() ||
            !values.DrugName?.trim() ||
            values.Amount === '' ||
            values.Amount === null ||
            values.Amount === undefined);
    const showFieldErrors = !manualMode;

    const handleAmountChange = (e) => {
        handleChange(e);
        const amount = Number(e.target.value);
        if (isRemainingQtyZero) {
            setError(remainingQtyErrorMessage);
        } else if (Number.isFinite(remainingLimit) && e.target.value !== '' && amount > remainingLimit) {
            setError(remainingQtyErrorMessage);
        } else {
            setError(null);
        }
    };

    const handleButtonClick = () => {
        if (isRemainingQtyZero) {
            setError(remainingQtyErrorMessage);
            return;
        }
        if (Number.isFinite(remainingLimit) && Number(values.Amount) > remainingLimit) {
            setError(remainingQtyErrorMessage);
            return;
        }
        if (Number.isFinite(dispensedLimit) && Number(values.Amount) > dispensedLimit) {
            setError(`Return quantity cannot be greater than dispensed quantity of ${dispensedLimit}`);
            return;
        }
        setError(null);
        handleSubmit();
    };

    return (
        <>
            <Card className="mb-4 no-radius-card">
                <CardBody>
                    {manualMode ? (
                        <>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="RxNumber">Rx #</Label>
                                        <Input
                                            type="text"
                                            name="RxNumber"
                                            id="RxNumber"
                                            placeholder="Rx #"
                                            value={values.RxNumber}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <div className="d-flex">
                                            <Label for="DispenseDate">Dispense Date</Label>
                                        </div>
                                        <DatePicker
                                            className={`form-control${
                                                showFieldErrors && errors.DispenseDate && touched.DispenseDate
                                                    ? ' is-invalid'
                                                    : ''
                                            }`}
                                            id="DispenseDate"
                                            name="DispenseDate"
                                            placeholderText="MM/DD/YYYY"
                                            dateFormat="MM/dd/yyyy"
                                            maxDate={new Date()}
                                            selected={values.DispenseDate ? new Date(values.DispenseDate) : null}
                                            onChange={(date) =>
                                                setFieldValue('DispenseDate', date ? date.toISOString() : '')
                                            }
                                            onBlur={handleBlur}
                                            autoComplete="off"
                                        />
                                        {showFieldErrors && errors.DispenseDate && touched.DispenseDate && (
                                            <div className="invalid-feedback d-block">{errors.DispenseDate}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <div className="d-flex">
                                            <Label for="ResidentName">
                                                Resident <span className="text-danger">*</span>
                                            </Label>
                                        </div>
                                        <Input
                                            type="text"
                                            name="ResidentName"
                                            id="ResidentName"
                                            placeholder="Last, First"
                                            value={values.ResidentName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            invalid={showFieldErrors && errors.ResidentName && touched.ResidentName}
                                        />
                                        {showFieldErrors && errors.ResidentName && touched.ResidentName && (
                                            <div className="invalid-feedback d-block">{errors.ResidentName}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <div className="d-flex">
                                            <Label for="DrugName">
                                                Drug Name <span className="text-danger">*</span>
                                            </Label>
                                        </div>
                                        <Input
                                            type="text"
                                            name="DrugName"
                                            id="DrugName"
                                            placeholder="Drug name"
                                            value={values.DrugName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            invalid={showFieldErrors && errors.DrugName && touched.DrugName}
                                        />
                                        {showFieldErrors && errors.DrugName && touched.DrugName && (
                                            <div className="invalid-feedback d-block">{errors.DrugName}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <div className="d-flex">
                                            <Label for="DispensedQuantity">Dispensed Quantity</Label>
                                        </div>
                                        <Input
                                            type="number"
                                            name="DispensedQuantity"
                                            id="DispensedQuantity"
                                            placeholder="Dispensed Qty"
                                            value={values.DispensedQuantity}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            invalid={
                                                showFieldErrors && errors.DispensedQuantity && touched.DispensedQuantity
                                            }
                                        />
                                        {showFieldErrors && errors.DispensedQuantity && touched.DispensedQuantity && (
                                            <div className="invalid-feedback d-block">{errors.DispensedQuantity}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <div className="d-flex">
                                            <Label for="RemainingQuantity">Remaining Quantity</Label>
                                        </div>
                                        <Input
                                            type="number"
                                            name="RemainingQuantity"
                                            id="RemainingQuantity"
                                            placeholder="Remaining Qty"
                                            value={values.RemainingQuantity}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            invalid={
                                                showFieldErrors && errors.RemainingQuantity && touched.RemainingQuantity
                                            }
                                        />
                                        {showFieldErrors && errors.RemainingQuantity && touched.RemainingQuantity && (
                                            <div className="invalid-feedback d-block">{errors.RemainingQuantity}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                            {isMissingManualFacility && (
                                <div className="text-danger mb-2">
                                    Please select a facility from the dropdown before adding.
                                </div>
                            )}
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <div className="d-flex">
                                            <Label for="Amount">
                                                Returned Quantity <span className="text-danger">*</span>
                                            </Label>
                                        </div>
                                        <Input
                                            type="number"
                                            name="Amount"
                                            id="Amount"
                                            placeholder="Return Quantity"
                                            value={values.Amount}
                                            onChange={handleAmountChange}
                                            onBlur={handleBlur}
                                            invalid={showFieldErrors && errors.Amount && touched.Amount}
                                        />
                                        {(isRemainingQtyZero || error) && (
                                            <div className="invalid-feedback d-block">
                                                {isRemainingQtyZero ? remainingQtyErrorMessage : error}
                                            </div>
                                        )}
                                        {showFieldErrors && errors.Amount && touched.Amount && !error && !isRemainingQtyZero && (
                                            <div className="invalid-feedback d-block">{errors.Amount}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                            {canCreate && (
                                <div className="d-flex justify-content-end">
                                    <Button
                                        style={{
                                            cursor: 'pointer',
                                            border: 'none',
                                            borderRadius: '5px',
                                            padding: '10px 10px',
                                            color: 'white',
                                            backgroundColor: '#6952AD',
                                        }}
                                        onClick={handleButtonClick}
                                        disabled={
                                            isRemainingQtyZero ||
                                            isRemainingQtyError ||
                                            isMissingManualFacility ||
                                            isManualEntryEmpty ||
                                            isManualRequiredFieldsMissing
                                        }>
                                        Add for Destruction Only
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <Row>
                            <Col md={4}>
                                <div className="mb-3">
                                    <div className="d-flex flex-column p-0">
                                        <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Rx #</p>
                                        <p>{rxNo}</p>
                                    </div>
                                    <div className="d-flex flex-column p-0">
                                        <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Resident</p>
                                        <p>{patName}</p>
                                    </div>
                                    <div className="d-flex flex-column p-0">
                                        <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Resident ID</p>
                                        <p>{patID}</p>
                                    </div>
                                    <div className="d-flex flex-column p-0">
                                        <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Remaining Quantity</p>
                                        <p>{searchRemainingQty != null && searchRemainingQty !== '' ? searchRemainingQty : '—'}</p>
                                    </div>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-3">
                                    <div className="d-flex flex-column p-0">
                                        <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Dispense Date</p>
                                        <p> {new Date(dispenseDt).toLocaleDateString()} </p>
                                    </div>
                                    <div className="d-flex flex-column p-0">
                                        <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Drug Name</p>
                                        <p>{drugLabelName}</p>
                                    </div>
                                    <div className="d-flex flex-column p-0">
                                        <p style={{ fontWeight: 'bold', marginBottom: '0' }}>Dispensed Quantity</p>
                                        <p>{qtyDispensed}</p>
                                    </div>
                                </div>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="Amount">
                                        Returned Quantity <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="number"
                                        name="Amount"
                                        id="Amount"
                                        placeholder="Return Quantity"
                                        value={values.Amount}
                                        onChange={handleAmountChange}
                                        onBlur={handleBlur}
                                        invalid={errors.Amount && touched.Amount ? true : false}
                                    />
                                    {(isRemainingQtyZero || error) && (
                                        <div className="invalid-feedback d-block">{isRemainingQtyZero ? remainingQtyErrorMessage : error}</div>
                                    )}
                                    {errors.Amount && touched.Amount && !error && !isRemainingQtyZero && (
                                        <div className="invalid-feedback d-block">{errors.Amount}</div>
                                    )}
                                </FormGroup>

                                {canCreate && (
                                    <div className="d-flex justify-content-end">
                                        <Button
                                            style={{
                                                cursor: 'pointer',
                                                border: 'none',
                                                borderRadius: '5px',
                                                padding: '10px 10px',
                                                color: 'white',
                                                backgroundColor: '#6952AD',
                                            }}
                                            onClick={handleButtonClick}
                                            disabled={isRemainingQtyZero || isRemainingQtyError}>
                                            Add
                                        </Button>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    )}
                </CardBody>
            </Card>
            {!manualMode && (
                <SelectDispenseDateModal
                    dispensedt={dispensedt}
                    setDispensedt={setDispensedt}
                    openDispenseDateModal={openDispenseDateModal}
                    setOpenDispenseDateModal={setOpenDispenseDateModal}
                    data={data}
                />
            )}
        </>
    );
}

export default AddRxForm;
